import Image from 'next/image';
import React, { useEffect, useCallback, useState, FC } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { useWindowSize } from 'react-use';
import { useTheme } from 'styled-components';
import { BodyText, H2, H4 } from '../../../ui/core/Typography/typography.styles';
import { useHasMounted } from '../../../hooks/useHasMounted';
import { processCmsImageUrl, processCmsMediaAltText, processCmsMediaUrl } from '../../../utils/urls';
import { ButtonGroup } from '../../../ui/core/Buttons/Buttons';
import Button from '../../../models/strapi/Button';
import { Icon, ICON_TYPE } from '../../IconComponent/IconComponent';
import { CircleNextBtn, CirclePrevBtn } from '../../EmblaCarousel/EmblaActionButtons.styles';
import { ProfileCard } from './ProfileCard';
import ProfileList from '../../../models/strapi/ProfileList';
import {
	InfoContainer,
	BackgroundMask,
	ColorMask,
	InfoSection,
	TopSection,
	InfoImage,
	InfoContent,
	BottomSection,
	BottomHead,
	CarouselButtons,
	CarouselSection,
	ViewRef,
	CarouselContent,
	BackgroundFade,
} from './InfoComponent.styles';
import { useDevicetype } from '../../../hooks/useDeviceType';
import { VideoEmbed } from '../../VideoEmbedComponent';

type InfoProps = {
	imageUrl: string;
	title: string;
	description: string;
	videoEmbedUrl?: string;
	button: Button;
	profiles?: ProfileList;
};

export const InfoComponent: FC<InfoProps> = ({ imageUrl, title, description, button, profiles, videoEmbedUrl }) => {
	const hasMounted = useHasMounted();
	const isBigList = profiles?.profileCards?.length > 4;
	const [viewportRef, embla] = useEmblaCarousel({
		active: true,
		skipSnaps: false,
		align: 'start',
		containScroll: 'trimSnaps',
		inViewThreshold: 1,
	});
	const deviceType = useDevicetype();

	const [prevBtnEnabled, setPrevBtnEnabled] = useState<boolean>(false);
	const [nextBtnEnabled, setNextBtnEnabled] = useState<boolean>(false);
	const scrollPrev = useCallback(() => {
		if (!embla) return;
		embla.scrollPrev();
	}, [embla]);
	const scrollNext = useCallback(() => {
		if (!embla) return;
		embla.scrollNext();
	}, [embla]);
	const onSelect = useCallback(() => {
		if (!embla) return;
		setPrevBtnEnabled(embla.canScrollPrev());
		setNextBtnEnabled(embla.canScrollNext());
	}, [embla]);

	useEffect(() => {
		if (embla) {
			embla.on('select', onSelect);
			embla.on('resize', onSelect);
			onSelect();
		}
	}, [embla, onSelect]);

	if (!hasMounted) {
		return null;
	}

	return (
		<InfoContainer>
			<BackgroundMask>
				<ColorMask id="mask1" />
				<ColorMask id="mask2" />
				<ColorMask id="mask3" />
				<ColorMask id="mask4" />
				<ColorMask id="mask5" />
			</BackgroundMask>
			<InfoSection>
				<TopSection>
					<InfoImage>{videoEmbedUrl ? <VideoEmbed url={videoEmbedUrl} /> : <Image alt="" src={processCmsImageUrl(imageUrl)} fill sizes="100vw" style={{ objectFit: 'cover' }} />}</InfoImage>
					<InfoContent>
						<H2>{title}</H2>
						<BodyText>{description}</BodyText>
						{button && <ButtonGroup text={button.text} type={button.type} url={button.url} />}
					</InfoContent>
				</TopSection>
				{profiles && (
					<BottomSection>
						<BottomHead>
							<H4>{profiles?.heading}</H4>
							<CarouselButtons>
								<CircleNextBtn onClick={scrollNext} disabled={!nextBtnEnabled}>
									<Icon type={ICON_TYPE.ARROW_RIGHT_L} />
								</CircleNextBtn>
								<CirclePrevBtn onClick={scrollPrev} disabled={!prevBtnEnabled}>
									<Icon type={ICON_TYPE.ARROW_RIGHT_L} />
								</CirclePrevBtn>
							</CarouselButtons>
						</BottomHead>
						<CarouselSection>
							<ViewRef ref={viewportRef}>
								<CarouselContent isRow={isBigList}>
									{profiles?.profileCards?.map((profile) => (
										<ProfileCard
											key={profile.id}
											isRow={isBigList}
											imageUrl={processCmsMediaUrl(deviceType === 'mobile', profile.image, profile.multimedia)}
											alternativeText={processCmsMediaAltText(deviceType === 'mobile', profile.image, profile.multimedia)}
											name={profile.name}
											description={profile.description}
										/>
									))}
								</CarouselContent>
							</ViewRef>
						</CarouselSection>
					</BottomSection>
				)}
			</InfoSection>
		</InfoContainer>
	);
};
